import { MarketMakerReportEligibilityStatusEnumValues, MarketMakerStatusEnumValues } from '@forgd/shared/enums'
import { z } from 'zod'

const DropRecordFiltersSchema = z.object({
  projectId: z.string().uuid().optional(),
  marketMakerId: z.string().optional(),
  exchangeId: z.string().optional(),
  baseCoinId: z.string().optional(),
  /** Current market maker status; omit rather than send empty */
  marketMakerStatus: z.enum(MarketMakerStatusEnumValues).optional(),
  /** Absent = every status for records, eligible only for aggregates */
  eligibilityStatus: z
    .preprocess(
      (val) => (typeof val === 'string' ? [val] : val),
      z.array(z.enum(MarketMakerReportEligibilityStatusEnumValues)),
    )
    .optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
})

export const GetDropRecordsQueryParams = DropRecordFiltersSchema.extend({
  limit: z.coerce.number().int().min(1).max(1000).optional(),
  offset: z.coerce.number().int().min(0).optional(),
})

export const DropRecordAggregateGroupByValues = ['project', 'marketMaker', 'project,marketMaker', 'exchange'] as const

export const GetDropRecordAggregatesQueryParams = DropRecordFiltersSchema.extend({
  groupBy: z.enum(DropRecordAggregateGroupByValues).optional(),
})

const metric = z.number().nullable()

/** The full quant drop row, so any admin surface can read the metrics without a contract change */
export const DropRecordSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  marketMakerId: z.string(),
  date: z.string(),
  createdAt: z.string().nullable(),
  fileId: z.string().nullable(),
  exchange: z.string().nullable(),
  exchangeId: z.string().nullable(),
  tradingPair: z.string().nullable(),
  instrumentType: z.string().nullable(),
  type: z.string().nullable(),
  baseCoinId: z.string().nullable(),
  baseCoinSymbol: z.string().nullable(),
  quoteCoinId: z.string().nullable(),
  quoteCoinSymbol: z.string().nullable(),
  eligibilityStatus: z.enum(MarketMakerReportEligibilityStatusEnumValues).nullable(),
  bidDepth50: metric,
  askDepth50: metric,
  bidAskDepth50: metric,
  kpiBidAskDepth50: metric,
  uptimeBidAskDepth50: metric,
  bidDepth100: metric,
  askDepth100: metric,
  bidAskDepth100: metric,
  kpiBidAskDepth100: metric,
  uptimeBidAskDepth100: metric,
  bidDepth200: metric,
  askDepth200: metric,
  bidAskDepth200: metric,
  kpiBidAskDepth200: metric,
  uptimeBidAskDepth200: metric,
  bidAskSpread: metric,
  kpiBidAskDepthSpread: metric,
  uptimeSpread: metric,
  orderFillVolumeMaker: metric,
  orderFillVolumeTaker: metric,
  orderFillVolumeTotal: metric,
  backupOrderFillVolumeMaker: metric,
  backupOrderFillVolumeTaker: metric,
  backupOrderFillVolumeTotal: metric,
  backupOrderFillVolume: z.boolean().nullable(),
  topOfBookPercentage: metric,
  bidDepthTob: metric,
  bidDepth10: metric,
  bidDepth25: metric,
  askDepthTob: metric,
  askDepth10: metric,
  askDepth25: metric,
  orderFillVolumeMakerTob: metric,
})

export const GetDropRecordsResponseBody = z.object({
  data: z.array(DropRecordSchema),
  pagination: z.object({
    totalCount: z.number().int(),
    count: z.number().int(),
    offset: z.number().int(),
    limit: z.number().int(),
    hasMore: z.boolean(),
  }),
})

/** Group keys that were not requested come back null */
export const DropRecordAggregateSchema = z.object({
  projectId: z.string().nullable(),
  marketMakerId: z.string().nullable(),
  exchangeId: z.string().nullable(),
  firstDate: z.string(),
  lastDate: z.string(),
  dayCount: z.number().int(),
  /** Latest day whose required drop fields were all present */
  lastCompleteDate: z.string().nullable(),
})

export const GetDropRecordAggregatesResponseBody = z.array(DropRecordAggregateSchema)

export type DropRecordType = z.infer<typeof DropRecordSchema>
export type GetDropRecordsResponseBodyType = z.infer<typeof GetDropRecordsResponseBody>
export type DropRecordAggregateType = z.infer<typeof DropRecordAggregateSchema>
export type GetDropRecordsQueryParamsType = z.infer<typeof GetDropRecordsQueryParams>
export type GetDropRecordAggregatesQueryParamsType = z.infer<typeof GetDropRecordAggregatesQueryParams>
